import Button from '@pelckmans/elementis/components/button';
import ButtonGroup from '@pelckmans/elementis/components/button-group';
import PinIcon from '@pelckmans/elementis/icons/pin';
import PinAltIcon from '@pelckmans/elementis/icons/pin-alt';
import ShareIcon from '@pelckmans/elementis/icons/share';
import { useContext } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import ModalContext from '../../../../contexts/ModalContext';
import ShareModal from '../../../../shared-components/share-modal/ShareModal';
import ModuleContext from '../../_contexts/ModuleContext';
import MedialinksContext from '../_contexts/MedialinksContext';
import PinnedMaterialContext, { type PinnedMaterialType } from '../_contexts/PinnedMaterialContext';
import SelectionContext from '../_contexts/SelectionContext';
import SlideSetsContext from '../_contexts/SlideSetsContext';
import TableOfContentsContext from '../_contexts/TableOfContentsContext';
import UserMaterialContext from '../_contexts/UserMaterialContext';

const BASE = 'PAGES.TOC_LEVEL.BULK_BAR';

export default function BulkBar() {
  const { t } = useTranslation();

  const { selectedUserMaterials, selectedSlideSets, selectedMedialinks, countSelection, clearSelection } =
    useContext(SelectionContext);

  const { pinnedMaterialList, pinMaterials, unpinMaterials } = useContext(PinnedMaterialContext);

  const { fetchMedialinks } = useContext(MedialinksContext);
  const { fetchUserMaterial } = useContext(UserMaterialContext);
  const { fetchSlideSets } = useContext(SlideSetsContext);

  const { showModal } = useContext(ModalContext);
  const { module } = useContext(ModuleContext);
  const { selectedNode } = useContext(TableOfContentsContext);

  if (countSelection() === 0) return null;

  const allSelectedMaterials = [
    ...selectedMedialinks.map(material => ({
      materialId: material.id,
      materialType: 'medialink' as PinnedMaterialType,
    })),
    ...selectedSlideSets.map(material => ({
      materialId: material.id,
      materialType: 'slide-set' as PinnedMaterialType,
    })),
    ...selectedUserMaterials.map(material => ({
      materialId: material.id,
      materialType: 'user-material' as PinnedMaterialType,
    })),
  ];

  const currentSelectionIsAllPinned = allSelectedMaterials.every(selected =>
    pinnedMaterialList.some(pinned => pinned.id === selected.materialId),
  );

  const selectedAllUnpinnedMaterials = allSelectedMaterials.filter(
    selected => !pinnedMaterialList.some(pinned => pinned.id === selected.materialId),
  );

  const onShareSubmitted = () => {
    if (selectedMedialinks.length > 0) fetchMedialinks();
    if (selectedUserMaterials.length > 0) fetchUserMaterial();
    if (selectedSlideSets.length > 0) fetchSlideSets();

    clearSelection();
  };

  const showShareModal = () => {
    if (!module || !selectedNode) return;

    return showModal(
      <ShareModal
        id="share-modal"
        moduleId={module.id}
        nodeId={selectedNode.id}
        userMaterials={selectedUserMaterials}
        slideSets={selectedSlideSets}
        medialinks={selectedMedialinks}
        onSubmitted={onShareSubmitted}
      />,
    );
  };

  return createPortal(
    <div className="bulk-bar-max-container">
      <dialog className="bulk-bar" aria-label={t(`${BASE}.A11Y_LABEL`)} open>
        <div className="bulk-bar__selected">
          {t(`${BASE}.MESSAGE`, { amount: countSelection() })}
          <Button size="sm" prominence="ghost" onClick={clearSelection}>
            {t(`${BASE}.ACTIONS.CLEAR.LABEL`)}
          </Button>
        </div>
        <ButtonGroup aria-label={undefined} role={undefined} size="sm">
          {!currentSelectionIsAllPinned && (
            <Button startIcon={<PinIcon />} onClick={() => pinMaterials(selectedAllUnpinnedMaterials)}>
              {t(`${BASE}.ACTIONS.PIN.LABEL`)}
            </Button>
          )}
          {currentSelectionIsAllPinned && (
            <Button startIcon={<PinAltIcon />} onClick={() => unpinMaterials(allSelectedMaterials)}>
              {t(`${BASE}.ACTIONS.UNPIN.LABEL`)}
            </Button>
          )}
          <Button startIcon={<ShareIcon />} onClick={showShareModal}>
            {t(`${BASE}.ACTIONS.SHARE.LABEL`)}
          </Button>
        </ButtonGroup>
      </dialog>
    </div>,
    document.getElementById('bulk-bar')!,
  );
}
